/**
 * Cosmetics v3 spotlight slider: vanilla custom element, no dependencies.
 *
 * Autoplay uses one timer at a time, scheduled after the previous transition ends. It pauses
 * on hover, keyboard focus, hidden tab, the pause control, and Theme Editor block selection.
 * Transitions never overlap. Reduced-motion users get no autoplay and no transition.
 * Listeners and timers are cleared on disconnect, so Theme Editor reloads never double-attach.
 */
const SWIPE_THRESHOLD = 40;
const RESUME_MULTIPLIER = 2;

class Cv3SpotlightSlider extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.track = this.querySelector('[data-cv3-spot-track]');
    this.slides = this.track ? [...this.track.querySelectorAll('[data-cv3-spot-slide]')] : [];
    this.viewport = this.querySelector('[data-cv3-spot-viewport]');
    this.dots = [...this.querySelectorAll('[data-cv3-spot-dot]')];
    this.prev = this.querySelector('[data-cv3-spot-prev]');
    this.next = this.querySelector('[data-cv3-spot-next]');
    this.pauseButton = this.querySelector('[data-cv3-spot-pause]');
    this.status = this.querySelector('[data-cv3-spot-status]');

    if (!this.track || this.slides.length < 2) return;

    this.count = this.slides.length;
    this.index = 0;
    this.interval = Number(this.dataset.interval) || 4000;
    this.anim = this.dataset.anim || 'slide';
    this.loop = this.dataset.loop === 'true';
    this.resumeAfter = this.dataset.resume === 'true';
    this.pauseOnHover = this.dataset.pauseHover === 'true';
    this.pauseOnFocus = this.dataset.pauseFocus === 'true';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isRtl = document.documentElement.dir === 'rtl';
    this.direction = this.isRtl ? -1 : 1;

    // Autoplay state. Each flag is independent, so a hover ending never restarts a user pause.
    this.autoplayAllowed = this.dataset.autoplay === 'true' && !this.reducedMotion;
    this.userPaused = false;
    this.interactionStopped = false;
    this.hovering = false;
    this.focused = false;
    this.editorSelected = false;
    this.busy = false;
    this.timer = null;
    this.resumeTimer = null;
    this.transitionTimer = null;
    this.dragStart = null;

    this.prev?.addEventListener('click', () => this.userStep(-1), { signal });
    this.next?.addEventListener('click', () => this.userStep(1), { signal });
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.userGoTo(i), { signal }));
    this.pauseButton?.addEventListener('click', () => this.togglePause(), { signal });

    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.addEventListener('focusin', (event) => {
      this.focused = event.target.matches(':focus-visible');
      this.schedule();
    }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) { this.focused = false; this.schedule(); }
    }, { signal });
    if (this.pauseOnHover) {
      this.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') { this.hovering = true; this.schedule(); } }, { signal });
      this.addEventListener('pointerleave', () => { this.hovering = false; this.schedule(); }, { signal });
    }

    if (this.viewport) {
      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
      this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
      this.viewport.addEventListener('pointercancel', () => { this.dragStart = null; }, { signal });
    }

    this.track.addEventListener('transitionend', (event) => {
      if (event.target === this.track) this.endTransition();
    }, { signal });

    document.addEventListener('visibilitychange', () => this.schedule(), { signal });

    // Theme Editor: selecting a slide shows it and holds autoplay until deselected.
    document.addEventListener('shopify:block:select', (event) => {
      const slide = event.target?.closest?.('[data-cv3-spot-slide]');
      if (!slide || !this.contains(slide)) return;
      this.editorSelected = true;
      this.showIndex(this.slides.indexOf(slide), false);
      this.schedule();
    }, { signal });
    document.addEventListener('shopify:block:deselect', (event) => {
      if (!this.contains(event.target)) return;
      this.editorSelected = false;
      this.schedule();
    }, { signal });

    this.resizeObserver = new ResizeObserver(() => this.layout());
    this.resizeObserver.observe(this.viewport || this.track);

    this.showIndex(0, false);
    this.schedule();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.clearTimers();
  }

  clearTimers() {
    if (this.timer) clearTimeout(this.timer);
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    if (this.transitionTimer) clearTimeout(this.transitionTimer);
    this.timer = null;
    this.resumeTimer = null;
    this.transitionTimer = null;
  }

  // Autoplay runs only when nothing holds it. Exactly one timer is ever scheduled.
  schedule() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (!this.autoplayAllowed || this.userPaused || this.interactionStopped) return;
    if (this.hovering || this.focused || this.editorSelected || document.hidden) return;
    this.timer = window.setTimeout(() => this.advance(1), this.interval);
  }

  advance(step) {
    // A transition is still running: wait for it to finish rather than overlapping it.
    if (this.busy) {
      this.timer = window.setTimeout(() => this.advance(step), 150);
      return;
    }
    const target = this.index + step;
    if (!this.loop && (target < 0 || target >= this.count)) {
      // Non-looping slider stops at the last slide.
      this.userPaused = true;
      this.syncPauseButton();
      return;
    }
    this.showIndex(((target % this.count) + this.count) % this.count, true);
  }

  // Manual actions stop autoplay. The setting decides whether it resumes after a while.
  userStep(step) {
    if (this.busy) return;
    this.stopForInteraction();
    const target = this.index + step;
    if (!this.loop && (target < 0 || target >= this.count)) return;
    this.showIndex(((target % this.count) + this.count) % this.count, true);
  }

  userGoTo(index) {
    if (this.busy || index === this.index) return;
    this.stopForInteraction();
    this.showIndex(index, true);
  }

  stopForInteraction() {
    if (!this.autoplayAllowed) return;
    this.interactionStopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    this.resumeTimer = null;
    if (this.resumeAfter) {
      this.resumeTimer = window.setTimeout(() => {
        this.interactionStopped = false;
        this.schedule();
      }, this.interval * RESUME_MULTIPLIER);
    }
  }

  togglePause() {
    this.userPaused = !this.userPaused;
    this.syncPauseButton();
    this.schedule();
  }

  syncPauseButton() {
    if (!this.pauseButton) return;
    this.pauseButton.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
    this.pauseButton.setAttribute('aria-label', this.userPaused ? this.dataset.playLabel || 'Play slideshow' : this.dataset.pauseLabel || 'Pause slideshow');
    this.dataset.paused = this.userPaused ? 'true' : 'false';
  }

  // Move to a slide. animate=false sets the state instantly (startup and editor selection).
  showIndex(index, animate) {
    if (index < 0 || index >= this.count || index === this.index && animate) return;
    this.index = index;

    const movingTransform = this.anim === 'slide' && animate && !this.reducedMotion;
    const animated = animate && !this.reducedMotion && this.anim !== 'none';
    this.track.style.transitionDuration = animated ? '' : '0s';

    if (this.anim === 'slide' || this.anim === 'none') {
      const stride = this.slides[0].getBoundingClientRect().width;
      this.track.style.transform = `translate3d(${-index * stride * this.direction}px, 0, 0)`;
    }
    if (this.anim === 'fade' || this.anim === 'fade_scale') {
      this.track.style.transform = 'none';
    }

    this.slides.forEach((slide, i) => {
      const current = i === index;
      slide.classList.toggle('is-current', current);
      slide.setAttribute('aria-hidden', current ? 'false' : 'true');
      slide.toggleAttribute('inert', !current);
    });

    this.dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });

    if (this.status) {
      this.status.textContent = (this.dataset.statusTemplate || '')
        .replace('#CURRENT#', String(index + 1))
        .replace('#TOTAL#', String(this.count));
    }

    if (animated && (movingTransform || this.anim === 'fade' || this.anim === 'fade_scale')) {
      this.busy = true;
      if (this.transitionTimer) clearTimeout(this.transitionTimer);
      // Fallback in case transitionend does not fire (for example, when the element is hidden).
      this.transitionTimer = window.setTimeout(() => this.endTransition(), this.transitionMs() + 80);
      // The next slide is scheduled from the start of this transition.
      this.schedule();
    } else {
      this.busy = false;
      this.schedule();
    }
  }

  transitionMs() {
    const value = getComputedStyle(this.track).transitionDuration || '0.6s';
    return parseFloat(value) * 1000 || 600;
  }

  endTransition() {
    if (this.transitionTimer) clearTimeout(this.transitionTimer);
    this.transitionTimer = null;
    this.busy = false;
  }

  layout() {
    if (this.anim !== 'slide' && this.anim !== 'none') return;
    const stride = this.slides[0].getBoundingClientRect().width;
    this.track.style.transitionDuration = '0s';
    this.track.style.transform = `translate3d(${-this.index * stride * this.direction}px, 0, 0)`;
    void this.track.offsetWidth;
    this.track.style.transitionDuration = '';
  }

  onKeydown(event) {
    if (event.target.closest('input, textarea, select')) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    // In RTL the logical "next" direction is visually leftward.
    const towardEnd = event.key === 'ArrowRight' ? 1 : -1;
    this.userStep(towardEnd * this.direction);
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.dragStart = { x: event.clientX, y: event.clientY };
  }

  onPointerUp(event) {
    if (!this.dragStart) return;
    const dx = (event.clientX - this.dragStart.x) * this.direction;
    const dy = Math.abs(event.clientY - this.dragStart.y);
    this.dragStart = null;
    if (dy > Math.abs(dx) || Math.abs(dx) < SWIPE_THRESHOLD) return;
    // A negative dx moved toward the logical start, so advance.
    this.userStep(dx < 0 ? 1 : -1);
  }
}

if (!customElements.get('cv3-spotlight-slider')) {
  customElements.define('cv3-spotlight-slider', Cv3SpotlightSlider);
}
